// Menu: a ⋮ button that opens a short list of actions (Campaigns cards and the campaign page, 31 Oct 2026). Escape or
// a click elsewhere closes it; choosing an item closes it and runs the item. Arrow keys move between items.
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { MoreVertical } from "lucide-react";

export interface MenuItem {
  label: string;
  onSelect: () => void;
  /** A destructive or final action, shown in the danger colour. */
  danger?: boolean;
  disabled?: boolean;
  /** A short line under the item ("Archiving pauses it first"). */
  note?: string;
}

export function Menu({ label, items }: { label: string; items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    box.current?.querySelector<HTMLButtonElement>("[role=menuitem]:not([disabled])")?.focus();
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);
  const keys = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      box.current?.querySelector<HTMLButtonElement>(".menu-btn")?.focus();
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const all = Array.from(box.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]:not([disabled])") ?? []);
    const at = all.indexOf(document.activeElement as HTMLButtonElement);
    all[(at + (e.key === "ArrowDown" ? 1 : all.length - 1)) % all.length]?.focus();
  };
  return (
    <div className="menu" ref={box} onKeyDown={keys}>
      <button type="button" className="menu-btn" aria-label={label} aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>
        <MoreVertical size={18} strokeWidth={1.75} aria-hidden="true" focusable="false" />
      </button>
      {open && (
        <div id={id} className="menu-pop" role="menu" aria-label={label}>
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              className={`menu-item${it.danger ? " menu-item-danger" : ""}`}
              disabled={it.disabled}
              onClick={() => {
                setOpen(false);
                it.onSelect();
              }}
            >
              {it.label}
              {it.note && <span className="menu-note">{it.note}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
